/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { ComponentInterface } from '@appCanvasCommon/interfaces/component.interface';
import { TicketContainer } from '../tickets/TicketContainer';
import { Texture } from 'pixi.js-legacy';
import { TicketWorkDay } from './TicketWorkDay';
import { ActualPhase } from '@touchplan/common';
import { CanvasAngularLand } from 'src/app/app-data/interfaces/canvas-angular-land';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FancyContainer } from '@appCanvasCommon/graphics/FancyContainer';
import { UntypedFormControl } from '@angular/forms';
import { TicketWorkDayPadlock } from '@appPlanApplication/ticket-components/TicketWorkDayPadlock';
import { EventMode } from '@appCanvasCommon/events/event-mode.enum';
import { TicketEditCommonDesktopService } from 'src/app/common/docked-ticket/services/ticket-edit-common-desktop.service';

export const WORK_DAY_DRAW_SETTINGS = {
	UNLOCKED_COLOR: '#777777',
	LOCKED_Y_POSITION: -48,
	UNLOCKED_Y_POSITION: -54,
};

export class TicketWorkDaysContainer extends FancyContainer implements ComponentInterface {
	public override renderable = true;
	private buttonTexture: Texture;
	private shadedTexture: Texture;
	// private startLock: TicketWorkDayPadlock;
	private dynamicLock: TicketWorkDayPadlock;

	private ticketEditCommonService: TicketEditCommonDesktopService;
	private canvasAngularLand: CanvasAngularLand;
	private ticketContainer: TicketContainer;
	private workDayCache: Map<number, TicketWorkDay> = new Map();
	private shutdownSubject$: Subject<boolean> = new Subject();
	private isEnabled = true;
	private control: UntypedFormControl = new UntypedFormControl(null);
	private _clicked = false;

	constructor(
		buttonTexture: Texture,
		shadedTexture: Texture,
		canvasAngularLand: CanvasAngularLand,
		ticketContainer: TicketContainer
	) {
		super();
		this.buttonTexture = buttonTexture;
		this.shadedTexture = shadedTexture;
		this.canvasAngularLand = canvasAngularLand;
		this.ticketContainer = ticketContainer;
		this.ticketEditCommonService = canvasAngularLand.ticketEditCommonService;

		this.ticketEditCommonService
			.getWorkdaysUiMetadata()
			.pipe(takeUntil(this.shutdownSubject$))
			.subscribe((editWorkdaysData) => {
				this.destroyPreviousWorkDays();
				if (!editWorkdaysData || !editWorkdaysData.liveWorkingDays || !editWorkdaysData.isActive) {
					if (this.dynamicLock) {
						this.destroyPadlocks();
					}
					this.visible = false;
					return null;
				}
				const data = ticketContainer.getData();
				if (editWorkdaysData.ticketId !== data.$id) {
					return null;
				}

				if (!this.dynamicLock) {
					this.createPadlocks();
				}

				const liveWorkingDays: boolean[] = [...editWorkdaysData.liveWorkingDays]; //...don't edit model data
				liveWorkingDays.forEach((day, idx) => {
					const newWorkDay = new TicketWorkDay(
						this.buttonTexture,
						this.shadedTexture,
						canvasAngularLand,
						ticketContainer,
						idx
					);
					this.workDayCache.set(idx, newWorkDay);

					this.addChild(newWorkDay);

					newWorkDay.x = idx * 145 + 8;
					newWorkDay.y = -48;
					newWorkDay.width = 131;
					newWorkDay.height = 24;
					// Always draw shaded texture for first work day
					if (idx === 0) {
						newWorkDay.setShaded();
					}

					if (day) {
						newWorkDay.setActive();
					} else {
						newWorkDay.setInactive();
					}
				});

				this.control.setValue(liveWorkingDays);

				this.handleAccessCheck();
				this.visible = true;
				// Mark canvas as a dirty boi
				this.canvasAngularLand.canvasConnector.application.markAsDirty();
			});

		this.control.valueChanges.pipe(takeUntil(this.shutdownSubject$)).subscribe((val) => {
			if (this._clicked) {
				this.ticketEditCommonService.applyWorkdaysToForm(val);
				this._clicked = false;
			}
		});

		this.alpha = 1.0;
		this.visible = false;
	}

	public disable(): void {
		this.eventMode = EventMode.Disabled;
		this.isEnabled = false;
		this.alpha = 0.5;
	}

	public enable(): void {
		this.eventMode = EventMode.Static;
		this.isEnabled = true;
		this.alpha = 1.0;
	}

	public toggleValue(idx: number): void {
		if (!this.isEnabled) {
			return;
		}
		const current = this.control.value;
		current[idx] = !current[idx];
		this._clicked = true;
		this.control.setValue(current);
	}

	public override destroy(options?) {
		this.destroyPreviousWorkDays();
		this.shutdownSubject$.next(true);
		this.buttonTexture = null;
		this.shadedTexture = null;
		this.canvasAngularLand = null;
		this.ticketContainer = null;
		super.destroy(options);
	}

	private destroyPreviousWorkDays(): void {
		this.workDayCache.forEach((thing) => {
			thing.destroy();
		});
		this.workDayCache.clear();
	}

	private destroyPadlocks(): void {
		// this.dynamicLock.destroy();
		//
		// // this.startLock = null;
		// this.dynamicLock = null;
	}

	private createPadlocks(): void {
		const data = this.ticketContainer.getData();
		// this.startLock = new TicketWorkDayPadlock(
		// 	utils.TextureCache[TICKET_TEXTURE_OPTIONS.TEXTURE_LOCK_ICON],
		// 	utils.TextureCache[TICKET_TEXTURE_OPTIONS.TEXTURE_UNLOCK_ICON],
		// 	this.canvasAngularLand,
		// 	this.ticketContainer
		// );
		// this.startLock.disable();
		// this.startLock.x = -32;
		// this.startLock.y = data.view.height + 32;

		// this.dynamicLock = new TicketWorkDayPadlock(
		// 	utils.TextureCache[TICKET_TEXTURE_OPTIONS.TEXTURE_LOCK_ICON],
		// 	utils.TextureCache[TICKET_TEXTURE_OPTIONS.TEXTURE_ROUNDED_CHECKBOX],
		// 	this.canvasAngularLand,
		// 	this.ticketContainer
		// );
		//
		// this.dynamicLock.x = data.view.width + 16;
		// if (this.etcSync.workingDaysHoldEndDate) {
		// 	this.dynamicLock.setLocked();
		// 	this.dynamicLock.y = WORK_DAY_DRAW_SETTINGS.LOCKED_Y_POSITION;
		// } else {
		// 	this.dynamicLock.setUnlocked();
		// 	this.dynamicLock.y = WORK_DAY_DRAW_SETTINGS.UNLOCKED_Y_POSITION;
		// }
		//
		// this.addChild(this.dynamicLock);
	}

	private handleAccessCheck(): void {
		let enabled = true;
		const data = this.ticketContainer.getData();

		if (
			this.canvasAngularLand.planAccessCheck.hasRestrictedAccessSync ||
			!this.canvasAngularLand.planAccessCheck.hasAccessToTicketSync(data)
		) {
			enabled = false;
		} else if (
			data.view.actualPhase === ActualPhase.plannedPreview ||
			data.view.actualPhase === ActualPhase.actualized
		) {
			enabled = false;
		}

		if (enabled) {
			// this.dynamicLock.enable();
			this.enable();
		} else {
			// this.dynamicLock.disable();
			this.disable();
		}
	}
}
